import React from "react";
import moment from "moment-timezone";
import { FuelType } from "@api/graphql/generated/generated-types";
import { SHORT_DATE_FORMAT } from "@src/common/lib/DateUtils";
import type { SurchargeAmountType } from "@api/graphql/generated/generated-types";
import { useCourierLocale } from "@src/common/courierLocale";

/**
 * Surcharge amount types priced from the EIA fuel rate. Mirrors
 * SurchargePricingAmountType.isFuel() in cube. Raw strings, not enum members:
 * suites that mock the generated types module would otherwise crash on this
 * module-level constant. currentEiaFuelRate is a plain
 * lookup that answers for any courier, so callers must decide whether a rate is
 * worth showing by checking the price's own surcharges against this list.
 */
export const DYNAMIC_FUEL_AMOUNT_TYPES: string[] = [
  "DYNAMIC_FUEL_SURCHARGE_BASED_ON_EIA_RATE",
  "DYNAMIC_FUEL_PERCENTAGE_ON_TOTAL_EXCEPT_SPECIFIED_SURCHARGES",
];

/**
 * Shape of the EIA rate a surcharge was priced from. Structurally satisfied by the
 * generated `EiaFuelRate` GraphQL type (whose Money/Date scalars are `any` — the wire
 * delivers ratePerGallon as a string and ratePeriod as "YYYY-MM-DD").
 */
export interface EiaFuelRateDisplay {
  /** Money scalar — arrives as a string over the wire. Use formatRatePerGallon. */
  ratePerGallon: number | string;
  percent: number;
  fuelType: FuelType;
  ratePeriod: string;
}

interface EiaFuelRateNoteProps {
  /** Null/undefined renders nothing — callers can pass item.eiaFuelRate directly. */
  eiaFuelRate?: EiaFuelRateDisplay | null;
  compact?: boolean;
}

export function fuelTypeLabel(fuelType: FuelType): string {
  return fuelType === FuelType.Diesel ? "diesel" : "gas";
}

/**
 * EIA fuel rates are published weekly for the Monday of each week, so the period
 * is shown as the week it covers rather than an exact day.
 */
export function formatRatePeriod(ratePeriod: string): string {
  const period = moment(ratePeriod, "YYYY-MM-DD");
  return period.isValid() ? period.format(SHORT_DATE_FORMAT) : ratePeriod;
}

/**
 * The Money scalar serializes to a string with trailing zeros stripped ("4.141", "4.1"),
 * so pad back to EIA's 3 decimals for display.
 */
export function formatRatePerGallon(ratePerGallon: number | string): string {
  const rate = Number(ratePerGallon);
  return isNaN(rate) ? String(ratePerGallon) : rate.toFixed(3);
}

/**
 * Shows the EIA fuel rate a dynamic fuel surcharge was priced from, so a CS rep can
 * answer "what are you charging me for gas?" without leaving the order.
 */
export function EiaFuelRateNote({
  eiaFuelRate,
  compact = false,
}: EiaFuelRateNoteProps) {
  const { currencyDisplay } = useCourierLocale();
  if (!eiaFuelRate) {
    return null;
  }
  const { ratePerGallon, percent, fuelType, ratePeriod } = eiaFuelRate;

  return (
    <div
      className={`text-slate-400 dark:text-gray-400 ${compact ? "text-xs" : "text-sm"}`}
    >
      <p>
        EIA {fuelTypeLabel(fuelType)} {currencyDisplay}
        {formatRatePerGallon(ratePerGallon)}/gal &middot; {percent}%
        <span className="text-slate-300 dark:text-gray-500">
          {" "}
          &middot; week of {formatRatePeriod(ratePeriod)}
        </span>
      </p>
    </div>
  );
}
